import { AIModeList } from '@flowus/common/ai-const';
import { Language, LanguageList, useLanguage } from '@flowus/common/hooks/use-language';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { detectRegionIsRu } from '@flowus/common/utils/system';
import type { FC, MouseEvent } from 'react';
import { useEffect } from 'react';
import { isWindows } from 'react-device-detect';
import { Button } from 'src/common/components/button';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { Switch } from 'src/common/components/switch';
import { LOCAL_KEY_THEME_MODE } from 'src/common/const';
import { useAIModel } from 'src/hooks/ai/use-ai-chat';
import { useCurrentUser } from 'src/hooks/user';
import { useFetchMe } from 'src/hooks/user/use-fetch-me';
import { clearBackupData, exportBackupData } from 'src/redux/middlewares/persistence';
import { setAppUiState, useThemeState } from 'src/services/app/hook';
import { setLocalStorage } from 'src/utils/local-storage';
import { ShortcutSystemSymbol } from 'src/utils/shortcut';
import { SettingModalCommon, useOpenDropdownList } from '../common';
import { useUpdateUserSetting } from '../helper';
import { Option } from '../option';
import { useCloseSettingModal } from '../use-open-setting-modal';
import { ClipperSetting } from './clipper-setting';
import { OpenPageMode, openPageModeList, themeList } from './const';
import { NotificationSetting } from './notification-setting';
import { PageLayoutSetting } from './page-layout-setting';
import { PageWidthSetting } from './page-width-setting';
import { TimezoneSetting } from './timezone-setting';

export const GeneralSetting: FC<{}> = () => {
  const { t } = useAppTranslation();
  const fetchMe = useFetchMe();
  const closeSettingModal = useCloseSettingModal();
  const openModal = useOpenModal();
  const theme = useThemeState();
  const currentUser = useCurrentUser();
  const updateUserSetting = useUpdateUserSetting();
  const selectTheme = themeList.find((i) => i.value === theme);
  // const beta = useLocalStateBeta();
  const openDropdownList = useOpenDropdownList();
  const { currentLanguage, changeLanguage } = useLanguage();

  const openMode = currentUser.setting?.openPageMode ?? OpenPageMode.JUMP;
  const selectedMode = openPageModeList.find((i) => i.value === openMode);

  const selectedAIMode = useAIModel();

  const openSelectList = (event: MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="next-modal w-36 py-2.5"
          items={themeList.map((i, index) => ({
            type: ListItemType.OPERATION,
            data: {
              title: i.title,
              value: i.value,
              selected: selectTheme?.value === i.value,
              tip: index === 0 ? undefined : `${ShortcutSystemSymbol}+Shift+L`,
            },
          }))}
          onItemClick={(item) => {
            setLocalStorage(LOCAL_KEY_THEME_MODE, item.data.value);
            setAppUiState({ $theme: item.data.value });
            onCloseModal();
          }}
        />
      ),
    });
  };

  const openSelectLanguage = async (event: MouseEvent) => {
    const isSystemIsRu = detectRegionIsRu.isRussian();

    const _list = LanguageList.filter((i) => {
      if (i.value === Language.ru) {
        if (!isSystemIsRu) {
          return false;
        }
      }
      return true;
    });

    openDropdownList({
      event,
      active: currentLanguage.value,
      list: _list,
      onClick: (item) => {
        changeLanguage(item.value);
        location.reload();
      },
    });
  };

  const openMethodMenu = (event: MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="next-modal w-36 py-2.5"
          items={openPageModeList.map((i, index) => ({
            type: ListItemType.OPERATION,
            data: {
              title: i.title,
              value: i.value,
              selected: openMode === i.value,
              tip:
                index === 0
                  ? undefined
                  : `${
                      isWindows
                        ? t('views.settings.general.alt_click')
                        : t('views.settings.general.option_click')
                    }+${t('common.click')}`,
            },
          }))}
          onItemClick={(item) => {
            void updateUserSetting({ openPageMode: item.data.value });
            onCloseModal();
          }}
        />
      ),
    });
  };

  const openAIModelMenu = (event: MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="next-modal w-36 py-2.5"
          items={AIModeList.map((i) => ({
            type: ListItemType.OPERATION,
            data: {
              title: i.title,
              value: i.value,
              selected: selectedAIMode.value === i.value,
            },
          }))}
          onItemClick={(item) => {
            void updateUserSetting({ aiModel: item.data.value });
            onCloseModal();
          }}
        />
      ),
    });
  };

  const exportLocalData = async () => {
    const id = 'exportLocalData';
    if (message.exists(id)) return;
    message.loading({ key: id, content: t('common.exporting_data') });
    await exportBackupData();
    await clearBackupData();
    message.success(t('views.settings.general.data_exported'));
    message.closeMessage(id);
  };

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  return (
    <>
      <div className={SettingModalCommon.commonStyles.container}>
        <SettingModalCommon.Header title={t('views.settings.general.title')} />

        <SettingModalCommon.Item
          leftChild={t('views.settings.general.theme')}
          rightChild={
            <>
              <SettingModalCommon.DropdownButton
                onClick={openSelectList}
                content={selectTheme?.title}
              />
            </>
          }
        />
        {!__PRIVATE__ && (
          <Option
            title={t('common.product_ai_title')}
            description={t('views.settings.general.ai_model')}
            rightPart={
              <SettingModalCommon.DropdownButton
                onClick={openAIModelMenu}
                content={selectedAIMode.title}
              />
            }
          />
        )}

        <SettingModalCommon.Item
          leftChild={t('views.settings.general.language')}
          rightChild={
            <>
              <SettingModalCommon.DropdownButton
                onClick={openSelectLanguage}
                content={currentLanguage.title}
              />
            </>
          }
        />

        {__BUILD_IN__ && <TimezoneSetting />}

        <SettingModalCommon.SettingDivider className="my-2.5" />

        <NotificationSetting />

        {!(__BUILD_IN__ || __PRIVATE__) && (
          <>
            <SettingModalCommon.SettingDivider className="mb-2.5" />
            <ClipperSetting />
          </>
        )}

        <Option
          title={t('views.settings.general.default_page_open_mode')}
          description={t('views.settings.general.default_page_open_mode_description')}
          rightPart={
            <SettingModalCommon.DropdownButton
              onClick={openMethodMenu}
              content={selectedMode?.title}
            />
          }
        />

        <Option
          title={t('views.settings.general.use_right_panel_for_new_pages')}
          description={t('views.settings.general.use_right_panel_for_new_pages_description')}
          rightPart={
            <Switch
              open={!!currentUser.setting?.useRightPageByNew}
              onSwitch={(status) => {
                void updateUserSetting({ useRightPageByNew: status });
              }}
            />
          }
        />

        <PageWidthSetting />
        <Option
          title={t('views.settings.general.new_page_directory')}
          description={t('views.settings.general.new_page_directory_description')}
          rightPart={
            <Switch
              open={!!currentUser.setting?.directoryMenu}
              onSwitch={(status) => {
                void updateUserSetting({ directoryMenu: status });
              }}
            />
          }
        />
        <PageLayoutSetting />
        <Option
          title={t('views.settings.general.content_preview_popup')}
          description={t('views.settings.general.content_preview_popup_description')}
          rightPart={
            <Switch
              open={
                currentUser.setting?.previewDialog === undefined ||
                currentUser.setting?.previewDialog
              }
              onSwitch={async (open) => {
                void updateUserSetting({
                  previewDialog: open,
                });
              }}
            />
          }
        />

        <Option
          title={t('views.settings.general.high_performance_mode')}
          description={t('views.settings.general.high_performance_mode_description')}
          rightPart={
            <Switch
              open={!!currentUser.setting?.enableHighPerformance}
              onSwitch={(status) => {
                void updateUserSetting({ enableHighPerformance: status });
              }}
            />
          }
        />
        <Option
          title={t('views.settings.general.export_local_error_log')}
          description={t('views.settings.general.export_local_error_log_description')}
          rightPart={<Button onClick={exportLocalData}>{t('common.export')}</Button>}
        />
        {/* {!__PRIVATE__ && (
          <Option
            title={'Beta 模式'}
            description="打开后，重启页面将会开启新版架构模式，适合解决卡顿或希望体验灰度功能的用户"
            rightPart={<Switch open={!!beta} onSwitch={(s) => $setLocalState({ beta: s })} />}
          />
        )} */}
      </div>

      <SettingModalCommon.FooterBar
        cancel={() => closeSettingModal()}
        cancelText={t('common.close')}
      />
    </>
  );
};
